/**
 * Saneamiento de los mensajes de error que llegan de la API antes de pintarlos.
 *
 * Regla de producto: al usuario NUNCA se le muestra un código técnico
 * (`WORKFLOW_ENROLLMENT_INVALID`, `ERR_TIMEOUT`), un nombre de columna
 * (`int_id_bussines`), un UUID suelto, una traza de Java/Angular ni
 * `[object Object]`. Se muestra el `message` humano del backend o, si no
 * lo hay, un texto por defecto en español acorde a la acción.
 *
 * Se aplica en DOS niveles:
 *  1. `authInterceptor` llama a `sanitizeApiErrorBody()` sobre el cuerpo de cada
 *     `HttpErrorResponse`. Así los ~600 `err.error?.message || 'texto por defecto'`
 *     que ya existen en la app caen solos a su texto por defecto cuando el backend
 *     manda un código en vez de una frase, sin tocar cada componente.
 *  2. `apiErrorMessage(err, fallback)` para código nuevo que quiera el mensaje ya
 *     resuelto de una sola llamada.
 *
 * El `code` original NO se toca: sigue disponible en `err.error.code` para quien
 * lo traduzca a un texto propio (p. ej. `TaskErrorService`), y el mensaje crudo
 * se conserva en `err.error.technicalMessage` + consola para depurar.
 */

/** SCREAMING_SNAKE_CASE: `WORKFLOW_ENROLLMENT_INVALID`, `ERR_TIMEOUT`, `TASK.NOT_FOUND`. */
const CODIGO_TECNICO = /^[A-Z][A-Z0-9]*(?:[._-][A-Z0-9]+)+$/;

/** UUID suelto como único contenido del mensaje. */
const UUID_SUELTO = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Nombre de columna del esquema (`int_id_bussines`, `str_name`, `dtm_created`). */
const COLUMNA_BD = /^(?:int|str|dtm|num|bit|dbl|bol|txt)_[a-z0-9_]+$/;

/** Traza de excepción de la JVM (`org.springframework...Exception: ...`). */
const EXCEPCION_JVM = /(?:^|\s)(?:java|javax|jakarta|org|io|reactor|com)\.[a-zA-Z0-9.$]+(?:Exception|Error)\b/;

/**
 * `true` si el texto es un código/traza técnica y no una frase para el usuario final.
 * Un mensaje vacío también cuenta como técnico (no aporta nada que mostrar).
 */
export function isTechnicalErrorText(text: unknown): boolean {
  if (typeof text !== 'string') return true;
  const t = text.trim();
  if (!t) return true;
  if (t === '[object Object]' || t === 'null' || t === 'undefined') return true;
  if (CODIGO_TECNICO.test(t)) return true;
  if (UUID_SUELTO.test(t)) return true;
  if (COLUMNA_BD.test(t)) return true;
  if (EXCEPCION_JVM.test(t)) return true;
  // Mensaje de red de Angular: "Http failure response for http://…: 500 OK".
  if (/^Http failure response/i.test(t)) return true;
  // Solo dígitos (un id o un código numérico suelto).
  if (/^\d+$/.test(t)) return true;
  return false;
}

/** Devuelve el texto solo si es legible para una persona; si no, `null`. */
export function humanText(text: unknown): string | null {
  return isTechnicalErrorText(text) ? null : (text as string).trim();
}

interface CuerpoError {
  message?: unknown;
  mensaje?: unknown;
  error?: unknown;
  detail?: unknown;
  title?: unknown;
  code?: unknown;
  technicalMessage?: unknown;
}

/**
 * Neutraliza en sitio los campos de texto del cuerpo de error que en realidad son
 * códigos técnicos, dejando el original en `technicalMessage`.
 *
 * Muta el objeto a propósito: es el cuerpo ya parseado de un `HttpErrorResponse`,
 * y así el saneamiento llega a todos los `err.error?.message` de la app.
 */
export function sanitizeApiErrorBody(body: unknown): void {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return;
  const cuerpo = body as CuerpoError;

  for (const campo of ['message', 'mensaje', 'detail', 'title'] as const) {
    const valor = cuerpo[campo];
    if (typeof valor !== 'string') continue;
    if (!isTechnicalErrorText(valor)) continue;

    if (cuerpo.technicalMessage === undefined && valor.trim()) {
      cuerpo.technicalMessage = valor;
    }
    // Se vacía (no se borra) para que el `|| 'texto por defecto'` de cada
    // componente entre en acción y la forma del objeto no cambie.
    cuerpo[campo] = '';
  }
}

/**
 * Mensaje listo para pintar en un toast/banner: el texto humano del backend o
 * `fallback` cuando lo único que vino fue un código técnico.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (!err || typeof err !== 'object') return fallback;

  const cuerpo = (err as { error?: unknown }).error;

  if (typeof cuerpo === 'string') {
    return humanText(cuerpo) ?? fallback;
  }

  if (cuerpo && typeof cuerpo === 'object') {
    const registro = cuerpo as CuerpoError;
    for (const campo of ['message', 'mensaje', 'detail', 'title'] as const) {
      const texto = humanText(registro[campo]);
      if (texto) return texto;
    }
  }

  return fallback;
}
